// "Which filters are active right now?" — the router's `state_query` kind.
//
// This is a QUESTION Otto can only answer from the CLIENT's own live state: the backend is stateless and its
// knowledge base documents how the platform WORKS, not what this user has selected this minute (which is why
// the question used to fall through to platform_qa and get the honest-but-useless "I don't have that
// documented yet"). So the server just routes — there is no second LLM call — and the answer is composed
// HERE, from the same applied-filter summary the removal path grounds against.
//
// Labels, not keys: the summary resolves each applied dimension's option IDs to the NAMES the filter window
// shows (see orchestrator.buildActiveSummary), so Otto reads back "Trade (HVAC)", never "trade_id: 42".
// Read-only by construction — nothing to confirm, nothing to apply, nothing to undo.
import { activeFilterSummaryText } from "./presenter";

// Wording for the empty case, kept out of the builder so tests and copy edits have one home.
export const NO_FILTERS_MESSAGE = "No filters are applied right now — you’re seeing everything in scope.";

/**
 * interpretResponse = { request_id, session_id, intent_kind: "state_query" } (no payload — see api/schemas.py).
 * ctx = { utterance, getActiveSummary } where `getActiveSummary()` resolves to the orchestrator's
 * [{ label, names: [] }] summary of what's applied. The thunk is injected rather than imported so this module
 * stays a leaf (the orchestrator imports the registry, which imports this — a direct import would be a cycle).
 *
 * Returns { filterState: true, groups, summaryText, message } — `message` is the spoken headline (and what a
 * history resume replays), `groups` is what the card lists. Best-effort: a summary that fails to build
 * degrades to "I couldn't read your current filters", never a thrown error in the chat.
 */
export const buildStateProposal = async (interpretResponse, ctx = {}) => {
    const base = {
        requestId: interpretResponse && interpretResponse.request_id,
        sessionId: interpretResponse && interpretResponse.session_id,
        utterance: ctx.utterance,
        filterState: true
    };

    let groups = [];
    let failed = false;
    try {
        groups = (typeof ctx.getActiveSummary === "function" ? await ctx.getActiveSummary() : []) || [];
    } catch (e) {
        failed = true;
    }
    groups = groups.filter(g => g && g.label);

    if (failed) {
        return {
            ...base,
            groups: [],
            summaryText: "",
            message: "I couldn’t read your current filters just now — try asking me again in a moment."
        };
    }
    if (!groups.length) {
        return { ...base, groups: [], summaryText: "", message: NO_FILTERS_MESSAGE };
    }

    const noun = groups.length === 1 ? "filter" : "filters";
    return {
        ...base,
        groups,
        // The same one-line prose the removal card uses for "your active filters are …" — carried so a
        // history resume (and the /turns/display report) replays the ANSWER, not just its headline.
        summaryText: activeFilterSummaryText(groups),
        message: `You have ${groups.length} ${noun} applied right now:`
    };
};
